import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Button,
  Input,
  Label,
  MessageBar,
  MessageBarActions,
  MessageBarBody,
  MessageBarTitle,
} from '@fluentui/react-components';
import { useInfiniteQuery } from '@tanstack/react-query';
import { Search } from 'lucide-react';

import RecoveryRevealDialog from '../components/RecoveryRevealDialog';

import {
  DeviceIndexEntry,
  DevicesListResponse,
  MIN_DEVICE_SEARCH_LENGTH,
  getDevices,
} from '../api/devices';
import { DataTable, DataTableColumn } from '../components/DataTable';
import RelativeTime from '../components/RelativeTime';
import { HeroBand } from '../components/abPrimitives';
import { IDENTIFIER, PLAIN_BUTTON, STROKE, SURFACE, TEXT } from '../theme/tokens';

// FD-283 (FD-ADR-031 §5) — Resources › Devices.
//
// The one thing this page does that nothing else could: find a serial across
// every campaign. Before FD-277 an operator holding a serial had to already know
// which campaign it was in to look it up.
//
// It deliberately shows THREE COLUMNS and no status. `DeviceIndex.Status` is
// written once at assignment and never updated, so a status column here would
// read "pending" for every device forever — worse than absent, because a stale
// value is indistinguishable from a true one. Live status is one click away on
// the device's own page.

const PAGE_SIZE = 50;

export default function DevicesListPage() {
  const [revealOpen, setRevealOpen] = useState(false);
  const navigate = useNavigate();

  // The query rides the URL so a search is shareable and survives reload — the
  // same reason the migrations list keeps its group filter there. Never
  // localStorage (FD-ADR-031 §1, inherited from ADR-015).
  const [searchParams, setSearchParams] = useSearchParams();
  const urlQuery = searchParams.get('q') ?? '';
  const [input, setInput] = useState(urlQuery);

  // Debounce so a serial typed at speed is one request, not eight.
  useEffect(() => {
    const handle = window.setTimeout(() => {
      const trimmed = input.trim();
      const next = new URLSearchParams(searchParams);
      if (trimmed) next.set('q', trimmed);
      else next.delete('q');
      // `replace` so a search does not stack twelve history entries an operator
      // then has to press Back through.
      setSearchParams(next, { replace: true });
    }, 300);
    return () => window.clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [input]);

  // Below the threshold the endpoint is not called with a query at all — a
  // one-character prefix would scan a whole partition to show noise.
  const effectiveQuery = urlQuery.length >= MIN_DEVICE_SEARCH_LENGTH ? urlQuery : undefined;
  const belowThreshold = urlQuery.length > 0 && urlQuery.length < MIN_DEVICE_SEARCH_LENGTH;

  const query = useInfiniteQuery<DevicesListResponse>({
    // `'list'` separates this infinite query's `{ pages, pageParams }` from the
    // PLAIN `['devices', { q, pageSize }]` lookups in `DeviceDetailPage` and
    // `GlobalSearch` (FD-485). Detail searches the serial it was given, so an
    // operator who typed a full serial here and clicked the row produced two
    // queries with the same hash and incompatible shapes. See the long note in
    // `MigrationsListPage` for the mechanism.
    queryKey: ['devices', 'list', { q: effectiveQuery, pageSize: PAGE_SIZE }],
    queryFn: ({ pageParam }) =>
      getDevices({
        q: effectiveQuery,
        pageSize: PAGE_SIZE,
        continuationToken: (pageParam as string | null | undefined) ?? undefined,
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.continuationToken ?? undefined,
  });

  const rows = useMemo<DeviceIndexEntry[]>(
    () => query.data?.pages.flatMap((p) => p.devices) ?? [],
    [query.data],
  );

  const columns = useMemo<DataTableColumn<DeviceIndexEntry>[]>(
    () => [
      {
        key: 'serialNumber',
        header: 'Serial number',
        numeric: true, // monospace — a serial is an identifier, not prose
        render: (d) => (
          <button
            type="button"
            onClick={() => navigate(`/devices/${encodeURIComponent(d.serialNumber)}`)}
            className={`${PLAIN_BUTTON} cursor-pointer text-left hover:underline`}
            style={{ color: TEXT.primary }}
          >
            {d.serialNumber}
          </button>
        ),
      },
      {
        key: 'migrationId',
        header: 'Campaign',
        render: (d) => (
          <button
            type="button"
            onClick={() => navigate(`/migrations/${encodeURIComponent(d.migrationId)}/overview`)}
            className={`${PLAIN_BUTTON} cursor-pointer text-left text-xs hover:underline`}
            style={{ color: TEXT.secondary, ...IDENTIFIER }}
            title={d.migrationId}
          >
            {d.migrationId}
          </button>
        ),
      },
      {
        key: 'assignedAt',
        header: 'Assigned',
        numeric: true,
        render: (d) =>
          d.assignedAt ? (
            <RelativeTime iso={d.assignedAt} />
          ) : (
            <span style={{ color: TEXT.secondary }}>Unknown</span>
          ),
      },
    ],
    [navigate],
  );

  return (
    <div className="flex flex-col gap-6">
      <HeroBand
        eyebrow="Resources"
        title="Devices"
        subtitle="Every device assigned to a campaign in this deployment. Search a serial to find which campaign it belongs to."
      />

      <div className="-mt-2 flex justify-end">
        {/* FD-313 (FD-ADR-033) — the reveal for a device that belongs to NO
            campaign anymore. A salvaged device that was removed and re-added (or
            just removed) is invisible to this list — the salvage registry is the
            only record of it — so the operator enters the serial directly. */}
        <Button size="small" onClick={() => setRevealOpen(true)}>
          Recover a salvaged device
        </Button>
      </div>

      <RecoveryRevealDialog open={revealOpen} onOpenChange={setRevealOpen} />

      <div className="flex max-w-md flex-col gap-1">
        <Label htmlFor="device-search" weight="semibold">
          Search by serial number
        </Label>
        <Input
          id="device-search"
          value={input}
          onChange={(_, data) => setInput(data.value)}
          placeholder="Start of a serial, e.g. MJ0"
          contentBefore={<Search size={14} strokeWidth={1.5} />}
        />
        {belowThreshold && (
          // Said out loud rather than silently returning nothing — an empty
          // result for a two-character query would read as "no such device".
          <span className="font-display text-xs" style={{ color: TEXT.secondary }}>
            Keep typing — search starts at {MIN_DEVICE_SEARCH_LENGTH} characters.
          </span>
        )}
      </div>

      <div style={{ border: `1px solid ${STROKE.base}`, backgroundColor: SURFACE.base }}>
        <DataTable
          columns={columns}
          data={rows}
          keyForRow={(d) => d.serialNumber}
          isLoading={query.isLoading}
          error={
            query.isError ? (
              <MessageBar intent="error">
                <MessageBarBody>
                  <MessageBarTitle>Could not load devices</MessageBarTitle>
                  {query.error instanceof Error ? query.error.message : 'Unexpected error'}
                </MessageBarBody>
                <MessageBarActions>
                  <Button size="small" onClick={() => query.refetch()}>
                    Retry
                  </Button>
                </MessageBarActions>
              </MessageBar>
            ) : undefined
          }
          emptyState={
            <span style={{ color: TEXT.secondary }}>
              {effectiveQuery
                ? `No device serial starts with “${effectiveQuery}”.`
                : 'No devices have been assigned to a campaign yet.'}
            </span>
          }
        />
        {query.hasNextPage && (
          <div className="flex justify-center py-3">
            <Button
              size="small"
              onClick={() => query.fetchNextPage()}
              disabled={query.isFetchingNextPage}
            >
              {query.isFetchingNextPage ? 'Loading…' : 'Load more'}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
